import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Mail, MessageCircle, Printer } from "lucide-react";
import { Link } from "react-router-dom";
import { api, money, openDownload, shopDateTime } from "@/lib/api";
import { AuthedImage } from "@/components/AuthedImage";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import PhotoCapture from "@/components/PhotoCapture";
import { SignaturePad } from "@/components/SignaturePad";
import { REPAIR_STATUS } from "@/pages/Repairs";
import { formatPhone } from "@/lib/phone";

const METHODS = ["CASH", "CARD", "BANK TRANSFER", "PAYID", "AFTERPAY"];
const NEXT = [
  { key: "in_progress", label: "Start work" },
  { key: "ready", label: "Ready for collection" },
  { key: "on_hold", label: "Put on hold" },
  { key: "booked_in", label: "Back to booked in" },
];

export default function RepairView() {
  const { id } = useParams();
  const { can } = useAuth();
  const [repair, setRepair] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [collectOpen, setCollectOpen] = useState(false);
  const [collect, setCollect] = useState({
    collected_by: "customer",
    collector_name: "",
    relationship: "",
    collector_phone: "",
    final_amount: "",
    amount_paid: "",
    method: "CASH",
    pay_in_full: true,
  });
  const [collectorId, setCollectorId] = useState([]);
  const [collectSig, setCollectSig] = useState("");

  const load = useCallback(() => {
    api
      .get(`/repairs/${id}`)
      .then(({ data }) => {
        setRepair(data);
        setLoadError(false);
      })
      .catch(() => {
        setLoadError(true);
        toast.error("Could not load that repair");
      });
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const fail = (err, fallback) =>
    toast.error(
      (err.response?.data?.detail && String(err.response.data.detail)) || fallback
    );

  const setStatus = async (status) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/repairs/${id}/status`, { status });
      setRepair(data);
      toast.success(`Marked as ${REPAIR_STATUS[status]?.label || status}`);
    } catch (err) {
      fail(err, "Could not change the status");
    } finally {
      setBusy(false);
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      const { data } = await api.post(`/repairs/${id}/note`, { text: note.trim() });
      setRepair(data);
      setNote("");
      toast.success("Note added");
    } catch (err) {
      fail(err, "Could not add that note");
    }
  };

  const savePhotos = async (items) => {
    try {
      const { data } = await api.put(`/repairs/${id}`, { attachments: items });
      setRepair(data);
    } catch (err) {
      fail(err, "Could not save those photos");
    }
  };

  const saveSignature = async (image) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/repairs/${id}/signature`, { image });
      setRepair(data);
      setSignOpen(false);
      toast.success("Signature saved to the job card");
    } catch (err) {
      fail(err, "Could not save that signature");
    } finally {
      setBusy(false);
    }
  };

  const notify = async () => {
    try {
      const { data } = await api.post(`/repairs/${id}/notify`);
      setRepair((r) => ({ ...r, notified_at: new Date().toISOString() }));
      toast.success(`Collection email sent to ${data.sent_to}`);
      load();
    } catch (err) {
      fail(err, "Could not send that email");
    }
  };

  const whatsAppReady = () => {
    const phone = (repair.customer_phone || "").replace(/\D/g, "");
    if (!phone) {
      toast.error("This repair has no phone number");
      return;
    }
    const code = String(repair.customer_country_code || "+61").replace(/\D/g, "");
    const intl = phone.startsWith(code) ? phone : `${code}${phone.replace(/^0+/, "")}`;
    const balance = Number(repair.balance || 0);
    const text =
      `Hi ${repair.customer_name || "there"}, your repair R${repair.repair_number} ` +
      `(${repair.item_description}) is ready to collect at Dhunna Jewellers ` +
      `${repair.branch}. ` +
      (balance > 0.005
        ? `Balance to pay on collection: $${balance.toFixed(2)}. `
        : "Nothing further to pay. ") +
      `Please bring photo ID. Thank you.`;
    window.open(`https://wa.me/${intl}?text=${encodeURIComponent(text)}`, "_blank");
  };

  const printCard = async () => {
    try {
      const res = await api.get(`/repairs/${id}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (err) {
      fail(err, "Could not build the job card");
    }
  };

  const doCollect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/repairs/${id}/collect`, {
        ...collect,
        final_amount: collect.final_amount === "" ? undefined : Number(collect.final_amount),
        amount_paid: collect.pay_in_full ? 0 : Number(collect.amount_paid) || 0,
        pay_in_full: collect.pay_in_full,
        collector_id_file_id: collectorId[0]?.file_id || "",
        signature: collectSig,
      });
      setRepair(data);
      setCollectOpen(false);
      toast.success("Collected — recorded who took it");
    } catch (err) {
      fail(err, "Could not complete the collection");
    } finally {
      setBusy(false);
    }
  };

  if (loadError)
    return (
      <div className="p-6" data-testid="repair-not-available">
        <p className="text-sm text-muted-foreground">
          This repair is not available. It may belong to another store or no longer exist.
        </p>
        <Link
          to="/repairs"
          className="mt-3 inline-block text-sm font-semibold text-[#C5A059] hover:underline"
          data-testid="repair-back-link"
        >
          ← Back to repairs
        </Link>
      </div>
    );
  if (!repair) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  const charge = repair.final_amount || repair.quote_amount || 0;
  const status = REPAIR_STATUS[repair.status] || { label: repair.status, cls: "bg-muted" };
  const closed = ["collected", "cancelled"].includes(repair.status);

  return (
    <div className="fade-up max-w-5xl" data-testid="repair-view">
      <Link
        to="/repairs"
        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground transition-colors duration-150 hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> All repairs
      </Link>

      <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold sm:text-4xl" data-testid="repair-number">
            R{repair.repair_number}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {repair.branch} · booked {shopDateTime(repair.created_at)} by {repair.created_by}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`px-3 py-1.5 text-xs font-bold uppercase ${status.cls}`}
            data-testid="repair-current-status"
          >
            {status.label}
          </span>
          {repair.balance > 0.005 ? (
            <span
              className="border border-[#9A3412] bg-[#FDF2EE] px-3 py-1.5 text-xs font-semibold text-[#9A3412]"
              data-testid="repair-balance-badge"
            >
              Owing {money(repair.balance)}
            </span>
          ) : (
            charge > 0 && (
              <span
                className="border border-[#166534] bg-[#F0FDF4] px-3 py-1.5 text-xs font-semibold text-[#166534]"
                data-testid="repair-paid-badge"
              >
                Paid in full
              </span>
            )
          )}
          <Button variant="outline" className="rounded-none" onClick={printCard} data-testid="repair-print-button">
            <Printer className="mr-1.5 h-4 w-4" /> Job card
          </Button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="border border-border p-5 lg:col-span-2">
          <h2 className="text-base font-bold md:text-lg">The job</h2>
          <dl className="mt-3 space-y-2 text-sm">
            {[
              ["Customer", `${repair.customer_name} · ${formatPhone(repair.customer_phone)}`],
              ["Email", repair.customer_email || "not on file"],
              ["Item", repair.item_description],
              ["Work required", repair.work_required || "-"],
              [
                "Purity / weight",
                `${repair.karat || "-"} · ${Number(repair.weight || 0).toFixed(3)} gm`,
              ],
              ["Promised by", repair.promised_date || "to be advised"],
            ].map(([k, v]) => (
              <div key={k} className="flex flex-wrap gap-2">
                <dt className="w-[130px] text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {k}
                </dt>
                <dd className="flex-1">{v}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="border border-border p-5">
          <h2 className="text-base font-bold md:text-lg">Money</h2>
          <dl className="mt-3 space-y-2 text-sm">
            {[
              ["Quoted", money(repair.quote_amount)],
              ["Charged", money(charge)],
              ["Platform handling fee", money(repair.platform_fee || 0)],
              [
                "Deposit",
                `${money(repair.deposit)}${
                  repair.deposit_method ? ` (${repair.deposit_method})` : ""
                }`,
              ],
              ["Paid", money(repair.total_paid)],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-2">
                <dt className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {k}
                </dt>
                <dd className="tabular">{v}</dd>
              </div>
            ))}
            <div className="flex justify-between gap-2 border-t border-border pt-2 font-bold">
              <dt>{repair.balance > 0.005 ? "Balance due" : "Settled"}</dt>
              <dd className="tabular" data-testid="repair-balance">
                {money(repair.balance)}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {!closed && (
        <div className="mt-5 flex flex-wrap gap-2" data-testid="repair-actions">
          {NEXT.filter((n) => n.key !== repair.status).map((n) => (
            <Button
              key={n.key}
              variant="outline"
              className="rounded-none"
              disabled={busy}
              onClick={() => setStatus(n.key)}
              data-testid={`repair-set-${n.key}`}
            >
              {n.label}
            </Button>
          ))}
          <Button
            className="rounded-none bg-[#C5A059] font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
            onClick={() => {
              setCollect((c) => ({
                ...c,
                final_amount: String(charge || ""),
                amount_paid: String(repair.balance > 0 ? repair.balance.toFixed(2) : ""),
                collector_name: "",
              }));
              setCollectOpen(true);
            }}
            data-testid="repair-collect-button"
          >
            Hand over / collect
          </Button>
          {repair.customer_email && (
            <Button
              variant="outline"
              className="rounded-none"
              onClick={notify}
              data-testid="repair-notify-button"
            >
              <Mail className="mr-1.5 h-4 w-4" /> Tell customer it's ready
            </Button>
          )}
          {repair.customer_phone && (
            <Button
              variant="outline"
              className="rounded-none border-[#1F6F43] text-[#1F6F43] hover:bg-[#F1FAF4]"
              onClick={whatsAppReady}
              data-testid="repair-whatsapp-button"
            >
              <MessageCircle className="mr-1.5 h-4 w-4" /> WhatsApp it's ready
            </Button>
          )}
          {!repair.signature_file_id && (
            <Button
              variant="outline"
              className="rounded-none"
              onClick={() => setSignOpen(true)}
              data-testid="repair-sign-button"
            >
              Customer signs drop-off
            </Button>
          )}
          {can("repairs:cancel") && (
            <Button
              variant="outline"
              className="rounded-none text-[#9A3412]"
              onClick={() => setStatus("cancelled")}
              data-testid="repair-cancel-button"
            >
              Cancel repair
            </Button>
          )}
        </div>
      )}

      {repair.collection && (
        <div className="mt-5 border border-[#166534] bg-[#F0FDF4] p-5" data-testid="repair-collection">
          <h2 className="text-base font-bold md:text-lg">Collected</h2>
          <p className="mt-2 text-sm">
            {repair.collection.collector_name}
            {repair.collection.collected_by === "other" && (
              <>
                {" "}
                collected on behalf of {repair.customer_name}
                {repair.collection.relationship ? ` (${repair.collection.relationship})` : ""}
                {repair.collection.collector_phone ? ` · ${repair.collection.collector_phone}` : ""}
              </>
            )}{" "}
            · {shopDateTime(repair.collection.at)} · handed over by {repair.collection.by}
          </p>
          <div className="mt-3 flex gap-3">
            {[repair.collection.collector_id_file_id, repair.collection.signature_file_id]
              .filter(Boolean)
              .map((fid, i) => (
                <button
                  type="button"
                  key={fid}
                  onClick={() =>
                    openDownload(`/files/${fid}`).catch(() =>
                      toast.error("Could not open that file")
                    )
                  }
                  className="border border-border bg-white"
                  data-testid={`repair-collection-file-${i}`}
                >
                  <AuthedImage
                    fileId={fid}
                    alt={i === 0 ? "Collector ID" : "Collector signature"}
                    className="h-[80px] w-[120px] object-contain"
                  />
                </button>
              ))}
          </div>
        </div>
      )}

      <div className="mt-5 border border-border p-5">
        <h2 className="text-base font-bold md:text-lg">Photos</h2>
        <div className="mt-3">
          <PhotoCapture
            items={repair.attachments || []}
            onChange={savePhotos}
            testid="repair-view-photos"
          />
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="border border-border p-5">
          <h2 className="text-base font-bold md:text-lg">Internal notes</h2>
          <p className="mt-1 text-xs text-muted-foreground">Staff only — never shown to the customer.</p>
          <div className="mt-3 space-y-3">
            {(repair.notes_log || []).length === 0 && (
              <p className="text-sm text-muted-foreground">No notes yet.</p>
            )}
            {(repair.notes_log || []).map((n, i) => (
              <div key={i} className="border-l-2 border-[#C5A059] pl-3" data-testid={`repair-note-${i}`}>
                <p className="text-sm">{n.text}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {n.by} · {shopDateTime(n.at)}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <Textarea
              className="min-h-[70px] rounded-none"
              placeholder="Add a note for the bench…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="repair-note-textarea"
            />
            <Button
              variant="outline"
              className="mt-2 rounded-none"
              onClick={addNote}
              data-testid="repair-add-note-button"
            >
              Add note
            </Button>
          </div>
        </div>

        <div className="border border-border p-5">
          <h2 className="text-base font-bold md:text-lg">History</h2>
          <div className="mt-3 space-y-3">
            {(repair.timeline || []).map((t, i) => (
              <div key={i} className="border-l-2 border-border pl-3" data-testid={`repair-event-${i}`}>
                <p className="text-sm font-semibold">
                  {REPAIR_STATUS[t.status]?.label || t.status}
                </p>
                {t.note && <p className="text-sm text-muted-foreground">{t.note}</p>}
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {t.by} · {shopDateTime(t.at)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {signOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="repair-sign-dialog">
          <div className="w-full max-w-lg border border-border bg-white p-6">
            <h2 className="text-base font-bold md:text-lg">Customer signs for drop-off</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Hand the screen to {repair.customer_name}.
            </p>
            <div className="mt-4">
              <SignaturePad
                onSave={saveSignature}
                onCancel={() => setSignOpen(false)}
                saving={busy}
                testidPrefix="repair-signature"
              />
            </div>
          </div>
        </div>
      )}

      {collectOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4" data-testid="repair-collect-dialog">
          <div className="my-8 w-full max-w-lg border border-border bg-white p-6">
            <h2 className="text-base font-bold md:text-lg">Hand the item over</h2>

            <div className="mt-4 flex gap-2">
              {[
                ["customer", "The customer"],
                ["other", "Someone else"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setCollect({ ...collect, collected_by: key })}
                  className={`flex-1 border px-3 py-2 text-sm transition-colors duration-150 ${
                    collect.collected_by === key
                      ? "border-[#C5A059] bg-[#FBF8F2] font-semibold"
                      : "border-border hover:bg-muted"
                  }`}
                  data-testid={`repair-collected-by-${key}`}
                >
                  {label}
                </button>
              ))}
            </div>

            {collect.collected_by === "other" && (
              <div className="mt-4 space-y-3" data-testid="repair-collector-fields">
                <div>
                  <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Their name *
                  </Label>
                  <Input
                    className="mt-1 h-10 rounded-none"
                    value={collect.collector_name}
                    onChange={(e) => setCollect({ ...collect, collector_name: e.target.value })}
                    data-testid="repair-collector-name"
                  />
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Relationship
                    </Label>
                    <Input
                      className="mt-1 h-10 rounded-none"
                      placeholder="Husband, son, friend…"
                      value={collect.relationship}
                      onChange={(e) => setCollect({ ...collect, relationship: e.target.value })}
                      data-testid="repair-collector-relationship"
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Their phone
                    </Label>
                    <Input
                      className="mt-1 h-10 rounded-none"
                      value={collect.collector_phone}
                      onChange={(e) => setCollect({ ...collect, collector_phone: e.target.value })}
                      data-testid="repair-collector-phone"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Photo of their ID *
                  </Label>
                  <div className="mt-2">
                    <PhotoCapture
                      items={collectorId}
                      onChange={setCollectorId}
                      required
                      testid="repair-collector-id"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Final price
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  className="mt-1 h-10 rounded-none text-right"
                  value={collect.final_amount}
                  onChange={(e) => setCollect({ ...collect, final_amount: e.target.value })}
                  data-testid="repair-final-amount"
                />
              </div>
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Paying now
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  className="mt-1 h-10 rounded-none text-right"
                  value={collect.pay_in_full ? "" : collect.amount_paid}
                  placeholder={collect.pay_in_full ? "Settling in full" : ""}
                  disabled={collect.pay_in_full}
                  onChange={(e) => setCollect({ ...collect, amount_paid: e.target.value })}
                  data-testid="repair-collect-amount"
                />
              </div>
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Method
                </Label>
                <Select
                  value={collect.method}
                  onValueChange={(v) => setCollect({ ...collect, method: v })}
                >
                  <SelectTrigger className="mt-1 h-10 rounded-none" data-testid="repair-collect-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none bg-white">
                    {METHODS.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <label className="mt-3 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={collect.pay_in_full}
                onChange={(e) => setCollect({ ...collect, pay_in_full: e.target.checked })}
                data-testid="repair-collect-pay-full"
              />
              Settle the balance in full (pays off whatever is still outstanding)
            </label>

            <div className="mt-4">
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Signature of the person collecting
              </Label>
              <div className="mt-2">
                {collectSig ? (
                  <div className="flex items-center gap-3">
                    <img
                      src={collectSig}
                      alt="Collector signature"
                      className="h-16 border border-border"
                      data-testid="repair-collect-signature-preview"
                    />
                    <Button
                      variant="outline"
                      className="rounded-none"
                      onClick={() => setCollectSig("")}
                      data-testid="repair-collect-signature-clear"
                    >
                      Sign again
                    </Button>
                  </div>
                ) : (
                  <SignaturePad
                    onSave={(img) => setCollectSig(img)}
                    testidPrefix="repair-collect-signature"
                  />
                )}
              </div>
            </div>

            <div className="mt-5 flex gap-2">
              <Button
                variant="outline"
                className="flex-1 rounded-none"
                onClick={() => setCollectOpen(false)}
                data-testid="repair-collect-cancel"
              >
                Cancel
              </Button>
              <Button
                className="flex-1 rounded-none bg-[#C5A059] font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
                onClick={doCollect}
                disabled={busy}
                data-testid="repair-collect-confirm"
              >
                Record collection
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
